//////////////////////////////////////////////////
// reducer with complex state, state that has interdepencies, business logic
//https://dev.to/spukas/3-reasons-to-usereducer-over-usestate-43ad
//https://www.robinwieruch.de/react-usereducer-vs-usestate
//
//https://medium.com/free-code-camp/why-you-should-choose-usestate-instead-of-usereducer-ffc80057f815


import { useCallback, useReducer } from "react";
import { ILoginInfoModel } from "../../services/login-data-service/Models";
import CustomerDataService from "../../services/customer-data-service/CustomerDataService";
import { LoginChangeCustomerStorageKey, RedirectFromLogoutStorageKey  } from "./LoginViewUtils";
import LoginDataService from "../../services/login-data-service/LoginDataService";
import { ILoginCustomer } from "../../common/models/LoginCustomer";
import { MfaSourceTypeEnum } from "../../common/enums/MfaSourceTypeEnum";
import storageService from "../../services/storage/storage-service/StorageService";
import { StorageKeys } from "../../services/storage/StorageKeys";

export enum LoginStepEnum {
    UserName = 1,
    Password = 2,
    SelectCustomer = 3,
    OtpChallenge = 4
}

export type LoginState = {
    //initialized: boolean,
    redirectInProgress: boolean,
    loginInfo: ILoginInfoModel | undefined,
    customers: ILoginCustomer[],
    step: LoginStepEnum;
    userName: string;
    userPassword: string;
    showPassword: boolean;
    valid: boolean;
    frameLoaded: boolean;
    rememberUser: boolean;
    isSSO: boolean;
    customerID: number;
    customerName: string;
    forgotPassword: boolean;
    //authenticating: boolean;
    useIDToken: boolean;
    showOtp: boolean;
    otp: string;
    mfaSource: MfaSourceTypeEnum;

    lastCustomerID: number | undefined;
};

// wrap initialState in a module so it can be mocked by the tests.  Specifically, control of
// redirectInProgress is needed because it is true by default and the state changes from
// callback methods from msal all of which are not publically accessible.
export const LoginViewStateUtils = {
    initialState: function (): LoginState {
        const loginState: LoginState = {
            //initialized: false,
            step: LoginStepEnum.UserName,
            userName: "",
            userPassword: "",
            showPassword: false,
            valid: false,
            frameLoaded: false,
            rememberUser: storageService.local.get<boolean>(StorageKeys.RememberUser, false),
            isSSO: storageService.local.get<boolean>(StorageKeys.IsSSO, false),
            //isSSO: window.localStorage.getItem(StorageKeys.IsSSO)?.toLowerCase() === 'true' ?? false,
            // customerID: storageService.local.get<number>(StorageKeys.LastCustomerID, 0),
            // customerName: storageService.local.get(StorageKeys.LastCustomerName, ""),
            customerID: storageService.session.get<number>(StorageKeys.LastCustomerID, 0),
            customerName: storageService.session.get(StorageKeys.LastCustomerName, ""),
            forgotPassword: false,
            customers: [],
            loginInfo: undefined,
            //redirectInProgress: true,,
            //redirectInProgress: storageService.session.get(RedirectFromLogoutStorageKey, false) ? false : true,
            redirectInProgress: window.sessionStorage.getItem(RedirectFromLogoutStorageKey)?.toLowerCase() === 'true' || false,
            //redirectInProgress: false,
            //authenticating: false,
            useIDToken: storageService.session.get(LoginChangeCustomerStorageKey, false),
            otp: "",
            showOtp: false,
            mfaSource: 0,
            lastCustomerID: storageService.local.get(StorageKeys.LastCustomerID, undefined),
        };

        loginState.userName = storageService.local.get<string>(StorageKeys.LastUserName, "");
        loginState.valid = loginState.userName.length > 0 && !loginState.isSSO;

        return loginState;
    }
}


enum LoginActionType {
    UserNameChanged = 'USERNAME_CHANGED',
    PasswordChanged = 'PASSWORD_CHANGED',
    OtpChanged = "OTP_CHANGED",
    ShowPasswordChanged = 'SHOW_PASSWORD_CHANGED',
    FrameLoaded = 'FRAME_LOADED',
    RememberUserChanged = 'REMEMBER_USER_CHANGED',
    SSOChanged = 'ELYNX_SSO_CHANGED',
    StepChanged = 'LOGIN_STEP_CHANGED',
    CustomerChanged = 'CUSTOMER_CHANGED',
    ValidChanged = 'VALID_CHANGED',
    ForgotPasswordChanged = 'FORGOT_PASSWORD_CHANGED',
    //InitializedChanged = 'INITIALIZED_CHANGED',
    CustomersChanged = 'CUSTOMERS_CHANGED',
    RedirectInProgressChanged = 'REDIRECT_IN_PROGRESS_CHANGED',
    LoginInfoChanged = 'LOGIN_INFO_CHANGED',
    AuthenticatingChanged = 'AUTHENTICATING_CHANGED',
    UseIDTokenChanged = 'USE_ID_TOKEN_CHANGED',
}

type LoginActions = | {
    type: LoginActionType.UserNameChanged;
    payload: string;
} | {
    type: LoginActionType.PasswordChanged;
    payload: string;
} | {
    type: LoginActionType.OtpChanged;
    payload: string;
} | {
    type: LoginActionType.ShowPasswordChanged;
    payload: boolean;
} | {
    type: LoginActionType.FrameLoaded;
    payload: boolean;
} | {
    type: LoginActionType.RememberUserChanged;
    payload: boolean;
} | {
    type: LoginActionType.SSOChanged;
    payload: boolean;
} | {
    type: LoginActionType.StepChanged;
    payload: LoginStepEnum;
} | {
    type: LoginActionType.CustomerChanged;
    payload: ILoginCustomer;
} | {
    type: LoginActionType.ValidChanged;
    payload: boolean;
} | {
    type: LoginActionType.ForgotPasswordChanged;
    payload: boolean;
} | {
//     type: LoginActionType.InitializedChanged;
//     payload: boolean;
// } | {
    type: LoginActionType.CustomersChanged;
    payload: ILoginCustomer[];
} | {
     type: LoginActionType.RedirectInProgressChanged;
     payload: boolean;
 } | {
    type: LoginActionType.LoginInfoChanged;
    payload: ILoginInfoModel;
// } | {
//     type: LoginActionType.AuthenticatingChanged;
//     payload: boolean;
} | {
   type: LoginActionType.UseIDTokenChanged;
    payload: boolean;
};

// This needs to be tracked outside of the state because in development/strict mode,
// reducer actions may be called twice. If this value were part of the state, then
// emails could go out twice in development.
let lastOtpEmailCustomerId = 0;

function checkOtp(customer: ILoginCustomer, state: LoginState) {
    const otpChallenge = (customer.showOTP ?? false);
    if (otpChallenge &&
        lastOtpEmailCustomerId !== customer.customerID &&
        ((customer.mfaSource ?? MfaSourceTypeEnum.Unconfigured) !== MfaSourceTypeEnum.Authenticator)) {
            LoginDataService.sendOtpEmail(customer.customerID, state.userName);
            lastOtpEmailCustomerId = customer.customerID;
    }
    return otpChallenge;
}

function loginViewReducer(state: LoginState, action: LoginActions): LoginState {
    switch (action.type) {
        case LoginActionType.UserNameChanged:
            // @@TODO validate valid email address

            const userName = action.payload.trim();

            if (userName.length) {
                storageService.local.set<string>(StorageKeys.LastUserName, userName);
                storageService.session.set<string>(StorageKeys.LastUserName, userName);
            } else {
                storageService.local.remove(StorageKeys.LastUserName);
                storageService.session.remove(StorageKeys.LastUserName);
            }


            return {
                ...state,
                userName: userName,
                valid: userName.length > 0
            };

        case LoginActionType.PasswordChanged:
            const password = action.payload.trim();
            return {
                ...state,
                userPassword: password,
                valid: password.length > 0
            };

        case LoginActionType.OtpChanged:
            const otp = action.payload.trim();
            return {
                ...state,
                otp: otp,
                valid: otp.length === 6
            };

        case LoginActionType.ShowPasswordChanged:
            return {
                ...state,
                showPassword: action.payload
            };

        case LoginActionType.FrameLoaded:
            return {
                ...state,
                frameLoaded: action.payload
            };

        case LoginActionType.RememberUserChanged:
            storageService.local.set(StorageKeys.RememberUser, action.payload);
            return {
                ...state,
                rememberUser: action.payload
            };

        case LoginActionType.SSOChanged:

            if (action.payload) {
                storageService.local.set(StorageKeys.RememberUser, true);
                storageService.local.set<string>(StorageKeys.LastUserName, state.userName);

                storageService.session.set(StorageKeys.RememberUser, true);
                storageService.session.set<string>(StorageKeys.LastUserName, state.userName);

                storageService.local.set(StorageKeys.IsSSO, action.payload);
            } else {
                storageService.local.remove(StorageKeys.IsSSO);
            }
            //console.log(`SSO -> ${action.payload}`);

            return {
                ...state,
                isSSO: action.payload,
                rememberUser: true,
                valid: false,
            };

        case LoginActionType.StepChanged: {

            let valid = false;
            let otpChallenge = false;
            let mfaSource: MfaSourceTypeEnum = state.mfaSource;

            if (action.payload === LoginStepEnum.UserName) {
                valid = state.userName.length > 0;
            } else if (action.payload === LoginStepEnum.SelectCustomer && state.customerID > 0) {
                const selectedCustomer = state.customers
                    ? state.customers.find(o => o.customerID === state.customerID)
                    : null;

                if (selectedCustomer) {
                    otpChallenge = checkOtp(selectedCustomer, state);
                }

                if (otpChallenge) {
                    mfaSource = selectedCustomer?.mfaSource ?? MfaSourceTypeEnum.Unconfigured;
                }

                valid = !otpChallenge;
            }

            return {
                ...state,
                step: action.payload,
                valid: valid,
                showOtp: otpChallenge,
                mfaSource: mfaSource
            };
        }
        case LoginActionType.CustomerChanged: {

            let otpChallenge = false;

            if (action.payload) {

                storageService.local.set(StorageKeys.LastCustomerID, action.payload.customerID);
                storageService.session.set(StorageKeys.LastCustomerID, action.payload.customerID);

                storageService.local.set(StorageKeys.LastCustomerName, action.payload.customerName);
                storageService.session.set(StorageKeys.LastCustomerName, action.payload.customerName);
                
                otpChallenge = checkOtp(action.payload, state);
            }

            return {
                ...state,
                customerID: action.payload.customerID,
                customerName: action.payload.customerName,
                showOtp: otpChallenge,
                mfaSource: action.payload.mfaSource ?? MfaSourceTypeEnum.Unconfigured,
                valid: action.payload.customerID > 0,
                lastCustomerID: action.payload.customerID
            };
        }

        case LoginActionType.ValidChanged:
            return {
                ...state,
                valid: action.payload
            };

        case LoginActionType.ForgotPasswordChanged:
            return {
                ...state,
                forgotPassword: action.payload
            };

        // case LoginActionType.InitializedChanged:
        //     return {
        //         ...state,
        //         initialized: action.payload
        //     };

        case LoginActionType.LoginInfoChanged:
            return {
                ...state,
                loginInfo: action.payload,
                customers: action.payload.customers
            };

        case LoginActionType.CustomersChanged:

            CustomerDataService.cacheCustomerList(action.payload);

            return {
                ...state,
                customers: action.payload,
                mfaSource: MfaSourceTypeEnum.Unconfigured,
                showOtp: false,
            };

        case LoginActionType.RedirectInProgressChanged:

            storageService.session.set(RedirectFromLogoutStorageKey, action.payload);
            return {
                ...state,
                redirectInProgress: action.payload
            };

        // case LoginActionType.AuthenticatingChanged:
        //     return {
        //         ...state,
        //         authenticating: action.payload
        //     };

        case LoginActionType.UseIDTokenChanged:
            if (!action.payload) {
                storageService.session.remove(LoginChangeCustomerStorageKey);
            }

            // we do not want to change the state for the duration of this login because
            // the authentication calls need it to determine the appropriate auth calls to make
            return state;

            // return {
            //     ...state,
            //     useIDToken: action.payload
            // };
   
        default:
            return state;
    }
}

export default function useLoginState() {

    // by using a custom hook we could include  other state to work on such as app settings state
    // also this simplifies use of dispatch by providing state methods, this would alleviate the
    // need to use an enum for the action types
    const [state, dispatch] = useReducer(loginViewReducer, LoginViewStateUtils.initialState());

    const setUserName = useCallback((userName: string) => dispatch({
            type: LoginActionType.UserNameChanged,
            payload: userName
        }), []);

    const setPassword = useCallback((password: string) => dispatch({
            type: LoginActionType.PasswordChanged,
            payload: password
        }), []);

    const setOtp = useCallback((otp: string) => dispatch({
            type: LoginActionType.OtpChanged,
            payload: otp
        }), []);

    const showPassword = useCallback((show: boolean) => dispatch({
            type: LoginActionType.ShowPasswordChanged,
            payload: show
        }), []);

    const setFrameLoaded = useCallback((loaded: boolean) => dispatch({
            type: LoginActionType.FrameLoaded,
            payload: loaded
        }), []);

    const rememberUser = useCallback((remember: boolean) => dispatch({
            type: LoginActionType.RememberUserChanged,
            payload: remember
        }), []);

    const setSSO = useCallback((sso: boolean) => dispatch({
            type: LoginActionType.SSOChanged,
            payload: sso
        }), []);

    const setStep = useCallback((step: LoginStepEnum) => dispatch({
            type: LoginActionType.StepChanged,
            payload: step
        }), []);

    const setCustomer = useCallback((customer: ILoginCustomer) => dispatch({
            type: LoginActionType.CustomerChanged,
            payload: customer
        }), []);

    const setValid = useCallback((valid: boolean) => dispatch({
            type: LoginActionType.ValidChanged,
            payload: valid
        }), []);

    const setForgotPassword = useCallback((forgotPassword: boolean) => dispatch({
            type: LoginActionType.ForgotPasswordChanged,
            payload: forgotPassword
        }), []);

    const setCustomers = useCallback((customers: ILoginCustomer[]) => dispatch({
            type: LoginActionType.CustomersChanged,
            payload: customers
        }), []);

    const setLoginInfo = useCallback((loginInfo: ILoginInfoModel) => dispatch({
        type: LoginActionType.LoginInfoChanged,
        payload: loginInfo
    }), []);

    const setRedirectInProgress = useCallback((inProgress: boolean) => dispatch({
        type: LoginActionType.RedirectInProgressChanged,
        payload: inProgress
    }), []);

    // const setAuthenticating = useCallback((authenticating: boolean) => dispatch({
    //         type: LoginActionType.AuthenticatingChanged,
    //         payload: authenticating
    //     }), []);

    const setUseIDToken = useCallback((authenticating: boolean) => dispatch({
        type: LoginActionType.UseIDTokenChanged,
        payload: authenticating
    }), []);

    return {
        state,
        setUserName,
        setPassword,
        showPassword,
        setFrameLoaded,
        rememberUser,
        setSSO,
        setStep,
        setCustomer,
        setValid,
        setForgotPassword,
        setCustomers,
        setLoginInfo,
        setRedirectInProgress,
        //setAuthenticating,
        setUseIDToken,
        setOtp,
    };
}
